import { RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { Skill } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  useRollbackSkillVersion,
  useSkillVersions,
} from "@/features/skills/hooks/useSkills";

interface Props {
  skill: Skill;
}

export function SkillVersionsTab({ skill }: Props) {
  const { t } = useTranslation("skills");
  const { t: tc } = useTranslation("common");
  const { data, isLoading } = useSkillVersions(skill.id);
  const rollback = useRollbackSkillVersion(skill.id);

  if (isLoading) {
    return (
      <p className="py-4 text-sm text-muted-foreground">
        {t("form.versions.loading")}
      </p>
    );
  }
  if (!data || data.length === 0) {
    return (
      <p className="py-4 text-sm text-muted-foreground">
        {t("form.versions.empty")}
      </p>
    );
  }

  async function handleRollback(version: number) {
    if (
      !window.confirm(
        tc("promptHistory.confirmRollback", {
          resource: t("form.titleEdit").toLowerCase(),
          version,
        }),
      )
    ) {
      return;
    }
    try {
      await rollback.mutateAsync({ version });
      toast.success(tc("promptHistory.rolledBack", { version }));
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <ul className="max-h-[60vh] space-y-2 overflow-y-auto py-4 pr-1">
      {data.map((v, i) => {
        const isCurrent = i === 0;
        return (
          <li key={v.version} className="rounded-md border p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-medium">
                  v{v.version}
                </span>
                {isCurrent ? (
                  <Badge variant="outline">{t("form.versions.current")}</Badge>
                ) : null}
              </div>
              {!isCurrent ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void handleRollback(v.version);
                  }}
                  disabled={rollback.isPending}
                  title={tc("promptHistory.rollbackToThis")}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  <span className="ml-1 text-xs">
                    {tc("promptHistory.rollbackToThis")}
                  </span>
                </Button>
              ) : null}
            </div>
            {v.commit_message ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {v.commit_message}
              </p>
            ) : null}
            <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-muted/50 p-2 text-xs">
              {v.content}
            </pre>
          </li>
        );
      })}
    </ul>
  );
}
